// Page « Google Form mariage » (référencement). En français : elle vise « google
// form mariage », « google form réponse mariage », « exemple google form mariage ».
// Pas-à-pas honnête de Google Forms, ses limites, puis l'alternative.

import { FaqItem } from '../common/GuideLayout';

export const UPDATED = '2026-10-15';
export const READING_MINUTES = 7;

export const TOC = [
  { id: 'pourquoi', label: 'Pourquoi Google Forms est tentant' },
  { id: 'creer', label: 'Créer un Google Form pour son mariage' },
  { id: 'exemple', label: 'Exemple de Google Form de mariage' },
  { id: 'limites', label: 'Les limites de Google Forms' },
  { id: 'comparatif', label: 'Google Form ou page RSVP ?' },
  { id: 'faq', label: 'Questions fréquentes' },
];

export const STEPS = [
  {
    title: 'Créez le formulaire',
    text: 'Sur forms.google.com, avec un compte Google. Donnez-lui un titre (« Mariage de [vos prénoms] ») et une description avec la date limite.',
  },
  {
    title: 'Ajoutez vos questions',
    text: 'Une question par information : nom, présence à chaque événement, menu, allergies (voir l’exemple ci-dessous).',
  },
  {
    title: 'Réglez les paramètres',
    text: 'Dans Paramètres, onglet Réponses : autorisez la modification des réponses si vos invités doivent pouvoir changer d’avis.',
  },
  {
    title: 'Reliez-le à Google Sheets',
    text: 'Dans l’onglet Réponses, associez le formulaire à une feuille de calcul pour suivre les réponses.',
  },
  {
    title: 'Partagez le lien',
    text: 'Bouton Envoyer, puis l’icône de lien (pensez à le raccourcir). Pour un QR code, passez par un générateur ou par Chrome.',
  },
];

// Exemple de formulaire : question → type de question Google Forms
export const EXAMPLE: Array<[string, string]> = [
  ['Votre prénom et votre nom', 'Réponse courte'],
  ['Serez-vous présent·e au vin d’honneur ?', 'Choix multiples : Oui · Non'],
  ['Serez-vous présent·e au dîner ?', 'Choix multiples : Oui · Non'],
  ['Combien serez-vous ?', 'Liste déroulante : 1 · 2 · 3 · 4…'],
  ['Votre menu', 'Choix multiples : Viande · Poisson · Végétarien'],
  ['Allergies ou régime alimentaire', 'Paragraphe (facultatif)'],
  ['Un mot pour les mariés', 'Paragraphe (facultatif)'],
];

export const LIMITS = [
  {
    title: 'Chacun tape son nom',
    text: '« Marie Dupont », « marie dupont », « Mme Dupont » : doublons et fautes de frappe, et aucun moyen de savoir qui n’a pas encore répondu.',
  },
  {
    title: 'La famille, mal gérée',
    text: 'Un seul formulaire pour plusieurs personnes : vous savez combien elles sont, mais pas qui mange quoi, ni qui vient à quel événement.',
  },
  {
    title: 'Tout le monde voit tout',
    text: 'Le même formulaire pour tous : impossible de ne proposer le brunch qu’à une partie des invités.',
  },
  {
    title: 'Un tableur à trier',
    text: 'Les réponses arrivent en lignes dans Google Sheets : à vous de compter, de dédoublonner et de relancer.',
  },
  {
    title: 'Rien après les réponses',
    text: 'Plan de table, menus pour le traiteur, budget : tout reste à refaire ailleurs, à la main.',
  },
];

export const COMPARE: Array<[string, string, string]> = [
  ['Prix', 'Gratuit', 'Gratuit'],
  [
    'Identifier l’invité',
    'Chacun tape son nom : doublons et fautes de frappe',
    'Chacun choisit son nom dans votre liste',
  ],
  [
    'Répondre pour la famille',
    'Un nombre de personnes, sans le menu de chacun',
    'Une réponse par personne, en une fois',
  ],
  ['Plusieurs événements', 'Les mêmes questions pour tous', 'Chacun répond aux événements où il est invité'],
  ['Modifier sa réponse', 'Si l’option est activée et le lien gardé', 'Oui, depuis la page'],
  ['Suivre les réponses', 'Un tableur à trier', 'Compteurs par événement, invités sans réponse'],
  ['Et après ?', 'Tout est à recopier ailleurs', 'Plan de table, menus pour le traiteur, budget'],
  ['Apparence', 'Le style Google Forms', 'Une page à vos prénoms, vos couleurs, votre photo'],
];

export const FAQ: FaqItem[] = [
  {
    q: 'Comment faire un Google Form pour la réponse à son mariage ?',
    a: 'Créez un formulaire sur forms.google.com, ajoutez une question par information (nom, présence à chaque événement, menu, allergies), autorisez la modification des réponses dans les paramètres, reliez-le à Google Sheets et partagez le lien.',
  },
  {
    q: 'Google Forms est-il gratuit pour un mariage ?',
    a: 'Oui, avec un compte Google. Une page de RSVP dédiée comme RSVP Please l’est aussi, sans les doublons ni le tableur à trier.',
  },
  {
    q: 'Les invités peuvent-ils modifier leur réponse sur Google Forms ?',
    a: 'Seulement si vous avez activé l’option dans les paramètres, et à condition qu’ils retrouvent le lien de modification reçu après leur réponse.',
  },
  {
    q: 'Comment éviter les doublons dans un Google Form de mariage ?',
    a: 'Google Forms peut limiter à une réponse par personne, mais vos invités doivent alors se connecter à un compte Google. Une page où chacun choisit son nom dans votre liste évite le problème.',
  },
  {
    q: 'Comment mettre un QR code vers un Google Form ?',
    a: 'Copiez le lien du formulaire et créez un QR code avec un générateur, ou depuis Chrome (menu de partage, « Créer un code QR »). Vérifiez qu’il s’agit d’un QR code statique, qui n’expire pas.',
  },
  {
    q: 'Quelle alternative gratuite à Google Forms pour un mariage ?',
    a: 'RSVP Please : une page d’invitation à vos prénoms, où chaque invité choisit son nom, répond pour chaque événement et pour sa famille, avec un QR code prêt à imprimer. Gratuit, sans publicité.',
  },
];
